import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useRouter } from 'next/router';

import Store from 'lib/Store';

import { useAlerts } from 'hooks/useAlerts';
import { useCountdown } from 'hooks/useCountdown';
import { useUser } from 'hooks/useUser';

import config from 'config/general';
import routes from 'config/routes';

import checkAccessCodeIsValid from 'api/auth/checkAccessCodeIsValid';
import sendCodeToEmail from 'api/auth/sendCodeToEmail';
import sendEmail from 'api/auth/sendEmail';
import acceptTerms from 'api/terms/acceptTerms';
import getTermsOfUse from 'api/terms/getTermsOfUse';
import getUserLogged from 'api/users/getUserLogged';

import {
  IAuthContextData,
  IAuthProviderData,
  ICheckCodeProps,
  IPreSignData,
  ISignInProps,
} from './types';

const accessStore = Store(`${config.localStorageKey}-access`);
const authStore = Store(`${config.localStorageKey}-auth`);

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

const AuthProvider: React.FC<IAuthProviderData> = ({ children }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const router = useRouter();

  const { errorMessage, confirmMessage } = useAlerts();
  const user = useUser();
  const { setCountdown } = useCountdown();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [accessData, setAccessData] = useState<IPreSignData>({
    email: accessStore.getItem('email') || '',
    token: accessStore.getItem('token') || '',
  });

  const langFormatted = useMemo(() => String(router.locale).toLowerCase(), [router.locale]);

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const sendEmailtMutation = useMutation(sendEmail);

  const checkEmail = useCallback(
    async ({ email }: ISignInProps): Promise<any> => {
      setLoading(true);

      const res = await sendEmailtMutation.mutateAsync({ email, lang: langFormatted });

      if (!res.success) {
        setLoading(false);

        return errorMessage(res?.data?.message);
      }

      accessStore.set({ email: res?.data?.email || email });

      setAccessData((prev) => ({
        ...prev,
        email: res?.data?.email || '',
      }));

      router.push(routes.auth().code);

      return setLoading(false);
    },
    [sendEmailtMutation, langFormatted, router, errorMessage],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const sendCodeMutation = useMutation(sendCodeToEmail);

  const sendVerificationCode = useCallback(
    async (document: string): Promise<any> => {
      if (!router.asPath.startsWith(routes.auth().code)) {
        setLoading(true);
      }

      const res = await sendCodeMutation.mutateAsync({ document, lang: langFormatted });

      if (!res?.success) {
        setLoading(false);

        console.log(res);

        return errorMessage(res?.data?.message || t(`sendCode:messages.error`));
      }

      setCountdown(60);
      router.push(routes.auth().code);

      return setLoading(false);
    },
    [router, sendCodeMutation, langFormatted, setCountdown, errorMessage, t],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const checkAccessCodeMutation = useMutation(checkAccessCodeIsValid);
  const userLoggedMutation = useMutation(getUserLogged);
  const acceptTermsMutation = useMutation(acceptTerms);

  const checkAccessCode = useCallback(
    async ({ code }: ICheckCodeProps): Promise<any> => {
      setLoading(true);

      const res = await checkAccessCodeMutation.mutateAsync({ token: code });

      if (!res?.success) {
        setLoading(false);

        return errorMessage(res?.message || t(`code:messages.invalid`));
      }

      authStore.setItem('token', res?.data?.access_token || '');
      authStore.setItem('isApp', '');
      accessStore.set({ email: res?.data?.email || '' });

      setAccessData((prev) => ({
        ...prev,
        token: res?.data?.access_token || '',
      }));

      const userData = await userLoggedMutation.mutateAsync(res?.data?.access_token);

      const message = t(`terms:messages:error`);

      if (!userData || !userData?.user.id) {
        setLoading(false);

        accessStore.clear();
        authStore.clear();
        user.clear();
        router.push(routes.auth().login);
        queryClient.invalidateQueries();

        return errorMessage(message);
      }

      user.saveUserDataOnReducer(userData);

      if (res?.data?.termAccepted === true) {
        setLoading(false);
        accessStore.clear();
        return router.push(routes.home);
      }

      setLoading(false);
      router.push(routes.auth().terms);
    },
    [checkAccessCodeMutation, userLoggedMutation, user, errorMessage, t, router],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const { data: dataTerms = null } = useQuery(
    ['getTermsOfUse', accessData.token],
    () => getTermsOfUse({ lang: String(router.locale), token: accessData.token }),
    {
      enabled: !!accessData.token,
    },
  );

  const acceptTermsOfUse = useCallback(async (): Promise<any> => {
    setLoading(true);

    const message = t(`terms:messages.error`);

    authStore.setItem('token', accessData.token);
    authStore.setItem('isApp', '');

    acceptTermsMutation.mutate({
      termId: Number(dataTerms?.termId),
      latestTermVersion: Number(dataTerms?.latestTermVersion),
      privacyId: Number(dataTerms?.privacyId),
      latestPrivacyVersion: Number(dataTerms?.latestPrivacyVersion),
    });

    const res = await userLoggedMutation.mutateAsync(accessData.token);

    if (!res || !res?.user.id) {
      setLoading(false);

      return errorMessage(message);
    }

    user.saveUserDataOnReducer(res);
    accessStore.clear();

    router.push(routes.home);

    return setLoading(false);
  }, [
    t,
    accessData.token,
    acceptTermsMutation,
    dataTerms?.termId,
    dataTerms?.latestTermVersion,
    dataTerms?.privacyId,
    dataTerms?.latestPrivacyVersion,
    userLoggedMutation,
    user,
    errorMessage,
    router,
  ]);

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const signOut = useCallback(
    (callback?: () => void) => {
      confirmMessage({
        message: t(`common:logout`),
        confirmButtonText: t('common:yes'),
        cancelButtonText: t('common:no'),
        onYesCallback: () => {
          accessStore.clear();
          authStore.clear();
          user.clear();
          router.push(routes.auth().login);
          queryClient.invalidateQueries();
          if (callback) callback();
        },
      });
    },
    [confirmMessage, queryClient, router, t, user],
  );

  // * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const contextValue = useMemo(
    () => ({
      isLoading,
      accessData,

      dataTerms,

      checkEmail,
      sendVerificationCode,
      checkAccessCode,
      acceptTermsOfUse,

      signOut,
    }),
    [
      isLoading,
      accessData,

      dataTerms,

      checkEmail,
      sendVerificationCode,
      checkAccessCode,
      acceptTermsOfUse,

      signOut,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

const useAuth = (): IAuthContextData => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export { AuthProvider, useAuth };
